import type {
  ComponentMaterial,
  RecyclingStream,
} from '@/shared/hooks/api/packagingSuppliers'
import { cn } from '@/shared/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupNumberInput,
  InputGroupText,
} from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type {
  ComponentDraft,
  ComponentEditorLabels,
} from './componentEditorTypes'
import { coerceRecyclingStreamForMaterial } from './componentMaterialRecyclingStreams'
import {
  buildMaterialOptions,
  buildRecyclingStreamOptionsForMaterial,
  getRecyclingPctErrorMessage,
  getWeightInputErrorMessage,
} from './componentEditorUtils'
import {
  isAllowedComponentRecyclingPctInput,
  isAllowedComponentWeightGramsInput,
} from './componentNumericInput'

type ComponentFormFieldsProps = {
  component: ComponentDraft
  onChange: (component: ComponentDraft) => void
  idPrefix: string
  labels: ComponentEditorLabels
  showValidationErrors: boolean
}

export function ComponentFormFields({
  component,
  onChange,
  idPrefix,
  labels,
  showValidationErrors,
}: ComponentFormFieldsProps) {
  const update = (patch: Partial<ComponentDraft>) => {
    onChange({ ...component, ...patch })
  }

  const materialOptions = buildMaterialOptions(labels)
  const recyclingStreamOptions = buildRecyclingStreamOptionsForMaterial(
    labels,
    component.material
  )
  const nameError =
    showValidationErrors && component.name.trim().length === 0
      ? labels.nameRequired
      : null
  const materialError =
    showValidationErrors && component.material === null
      ? labels.materialRequired
      : null
  const weightError = showValidationErrors
    ? getWeightInputErrorMessage(component.weightInput, labels)
    : null
  const recyclingPctError =
    showValidationErrors && component.recyclable
      ? getRecyclingPctErrorMessage(component.recyclingPctInput, labels)
      : null

  const isRecyclable = component.recyclable
  const hasMaterialSelected = component.material !== null
  const recyclingFieldsDisabled = !isRecyclable || !hasMaterialSelected
  const hasNameError = nameError != null
  const hasMaterialError = materialError != null
  const hasWeightError = weightError != null
  const hasRecyclingPctError = recyclingPctError != null
  const nameErrorId = `${idPrefix}-name-error`
  const materialErrorId = `${idPrefix}-material-error`
  const weightErrorId = `${idPrefix}-weight-error`
  const recyclingPctErrorId = `${idPrefix}-recycling-pct-error`

  return (
    <div className='grid gap-3'>
      <div className='space-y-2'>
        <Label htmlFor={`${idPrefix}-name`}>{labels.name}</Label>
        <Input
          id={`${idPrefix}-name`}
          value={component.name}
          aria-invalid={hasNameError}
          aria-describedby={hasNameError ? nameErrorId : undefined}
          onChange={(event) => {
            update({ name: event.target.value })
          }}
          placeholder={labels.namePlaceholder}
        />
        {hasNameError && (
          <p id={nameErrorId} className='text-sm text-destructive'>
            {nameError}
          </p>
        )}
      </div>

      <div className='space-y-2'>
        <Label
          htmlFor={`${idPrefix}-material`}
          className={cn(hasMaterialError && 'text-destructive')}
        >
          {labels.material}
        </Label>
        <Select
          value={component.material ?? undefined}
          onValueChange={(value) => {
            if (value) {
              const material = value as ComponentMaterial
              update({
                material,
                recyclingStream: coerceRecyclingStreamForMaterial(
                  material,
                  component.recyclingStream
                ),
              })
            }
          }}
          items={materialOptions}
        >
          <SelectTrigger
            id={`${idPrefix}-material`}
            className='w-full'
            aria-invalid={hasMaterialError}
            aria-describedby={hasMaterialError ? materialErrorId : undefined}
          >
            <SelectValue placeholder={labels.selectMaterial} />
          </SelectTrigger>
          <SelectContent>
            {materialOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasMaterialError && (
          <p id={materialErrorId} className='text-sm text-destructive'>
            {materialError}
          </p>
        )}
      </div>

      <div className='space-y-2'>
        <Label htmlFor={`${idPrefix}-weight`}>{labels.weightGrams}</Label>
        <InputGroup>
          <InputGroupNumberInput
            id={`${idPrefix}-weight`}
            autoComplete='off'
            value={component.weightInput}
            allowNegative={false}
            decimalScale={4}
            isAllowed={isAllowedComponentWeightGramsInput}
            aria-invalid={hasWeightError}
            aria-describedby={hasWeightError ? weightErrorId : undefined}
            onValueChange={({ value }) => {
              update({ weightInput: value })
            }}
          />
          <InputGroupAddon align='inline-end'>
            <InputGroupText>g</InputGroupText>
          </InputGroupAddon>
        </InputGroup>
        {hasWeightError && (
          <p id={weightErrorId} className='text-sm text-destructive'>
            {weightError}
          </p>
        )}
      </div>

      <hr />

      <label className='flex items-center gap-2 text-sm'>
        <Checkbox
          checked={component.recyclable}
          onCheckedChange={(checked) => {
            update({ recyclable: checked === true })
          }}
        />
        {labels.recyclable}
      </label>

      <div className={cn('space-y-1', recyclingFieldsDisabled && 'opacity-60')}>
        <Label
          htmlFor={`${idPrefix}-stream`}
          className={cn(recyclingFieldsDisabled && 'text-muted-foreground')}
        >
          {labels.recyclingStream}
        </Label>
        <Select
          value={component.recyclingStream ?? 'NONE'}
          disabled={recyclingFieldsDisabled}
          onValueChange={(value) => {
            if (value) {
              update({
                recyclingStream:
                  value === 'NONE' ? null : (value as RecyclingStream),
              })
            }
          }}
          items={recyclingStreamOptions}
        >
          <SelectTrigger
            id={`${idPrefix}-stream`}
            className='w-full'
            disabled={recyclingFieldsDisabled}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {recyclingStreamOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={cn('space-y-1', recyclingFieldsDisabled && 'opacity-60')}>
        <Label
          htmlFor={`${idPrefix}-recycling-pct`}
          className={cn(recyclingFieldsDisabled && 'text-muted-foreground')}
        >
          {labels.recyclingPercentage}
        </Label>
        <InputGroup>
          <InputGroupNumberInput
            id={`${idPrefix}-recycling-pct`}
            autoComplete='off'
            disabled={recyclingFieldsDisabled}
            value={component.recyclingPctInput}
            allowNegative={false}
            decimalScale={4}
            isAllowed={isAllowedComponentRecyclingPctInput}
            aria-invalid={hasRecyclingPctError}
            aria-describedby={
              hasRecyclingPctError ? recyclingPctErrorId : undefined
            }
            onValueChange={({ value }) => {
              update({ recyclingPctInput: value })
            }}
          />
          <InputGroupAddon align='inline-end'>
            <InputGroupText>%</InputGroupText>
          </InputGroupAddon>
        </InputGroup>
        {hasRecyclingPctError && (
          <p id={recyclingPctErrorId} className='text-sm text-destructive'>
            {recyclingPctError}
          </p>
        )}
      </div>

      <hr />

      <label className='flex items-center gap-2 text-sm'>
        <Checkbox
          checked={component.reusable}
          onCheckedChange={(checked) => {
            update({ reusable: checked === true })
          }}
        />
        {labels.reusable}
      </label>

      <hr />

      <div className='space-y-2'>
        <Label htmlFor={`${idPrefix}-notes`}>{labels.notes}</Label>
        <Textarea
          id={`${idPrefix}-notes`}
          value={component.notes ?? ''}
          onChange={(event) => {
            update({
              notes: event.target.value.trim() || null,
            })
          }}
          placeholder={labels.notesPlaceholder}
        />
      </div>
    </div>
  )
}
